<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import { default as PButton } from 'primevue/button'
import { default as PButtonGroup } from 'primevue/buttongroup'
import { default as PSelect } from 'primevue/select'
import { default as PTextarea } from 'primevue/textarea'

// VUE
import { computed, type ModelRef } from 'vue'
import { generateDataOptionsFromEnum } from '@/presentation/composables/dataOptions.ts'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'

const emit = defineEmits(['edit', 'duplicate', 'delete', 'click', 'type-change'])
const question: ModelRef<QuizQuestion> = defineModel('question', { required: true })

const props = defineProps<{
  isEditing: boolean
  index: number
  canModifyStructure: boolean
  canModifyContent: boolean
}>()

const questiontypeOptions = computed(() => {
  return generateDataOptionsFromEnum(LearningQuizQuestionType.Values, (a) =>
    LearningQuizQuestionType.label(a as LearningQuizQuestionType.Values)
  )
})

function getQuestionTypeLabel(type: LearningQuizQuestionType.Values): string {
  return LearningQuizQuestionType.label(type)
}

function handleUpdate(event: any) {
  // Émettre l'événement de changement de type au parent
  if (props.canModifyStructure) {
    emit('type-change', {
      index: props.index,
      oldType: question.value.type,
      newType: event.value,
      question: question.value
    })
  }
}

function handleEdit() {
  emit('edit', question.value)
}

function handleClick() {
  // Même comportement que le bouton edit
  emit('click', props.index)
}

function handleDuplicate() {
  emit('duplicate', question.value)
}

function handleDelete() {
  emit('delete', props.index)
}
</script>

<template>
  <div
    class="flex w-full cursor-pointer flex-col gap-4 rounded-md border p-6 transition-all duration-200 hover:shadow-md"
    :class="{
      'border-blue-500 bg-blue-50': isEditing,
      'border-gray-200 bg-white hover:border-gray-300': !isEditing
    }"
    @click="handleClick"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <font-awesome-icon
          icon="fa-solid fa-grip-vertical"
          class="handle cursor-move text-gray-400"
        />
        <h5 class="text-lg font-semibold">Question {{ props.index + 1 }}</h5>
        <span v-if="isEditing" class="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">
          En cours d'édition
        </span>
      </div>
      <div class="flex items-center gap-2" @click.stop>
        <p-button-group>
          <p-button
            icon="pi pi-pencil"
            outlined
            size="small"
            @click="handleEdit"
            :disabled="!canModifyContent"
            severity="secondary"
          />
          <p-button
            icon="pi pi-copy"
            outlined
            size="small"
            @click="handleDuplicate"
            :disabled="!canModifyStructure"
            severity="secondary"
          />
          <p-button
            icon="pi pi-trash"
            outlined
            size="small"
            @click="handleDelete"
            :disabled="!canModifyStructure"
            severity="danger"
          />
        </p-button-group>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <!-- Type de question -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium">Type de question</label>
        <div v-if="!isEditing" class="pointer-events-none text-sm">
          {{ getQuestionTypeLabel(question.type) || 'Type non défini' }}
        </div>
        <p-select
          v-else
          v-model="question.type"
          :options="questiontypeOptions"
          option-label="label"
          option-value="value"
          placeholder="Sélectionner un type"
          :disabled="!canModifyStructure"
          @change="handleUpdate"
        />
      </div>

      <!-- Libellé de la question -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium">Libellé de la question</label>
        <div v-if="!isEditing" class="text-sm">
          <span v-if="question.label" class="whitespace-pre-wrap">{{ question.label }}</span>
          <span v-else class="text-gray-500 italic">Aucun libellé défini</span>
        </div>
        <p-textarea
          v-else
          v-model="question.label"
          placeholder="Saisir le libellé de la question"
          :disabled="!canModifyContent"
          rows="3"
          auto-resize
        />
      </div>

      <!-- Indicateurs visuels pour les contraintes -->
      <div
        v-if="isEditing && !canModifyStructure && canModifyContent"
        class="text-xs text-amber-600"
      >
        <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-1" />
        Seuls les libellés peuvent être modifiés sur cette version
      </div>

      <div v-if="isEditing && !canModifyContent" class="text-xs text-red-600">
        <font-awesome-icon icon="fa-solid fa-lock" class="mr-1" />
        Version verrouillée - aucune modification autorisée
      </div>
    </div>
  </div>
</template>
